/**
 * Static tag + performance tag chips for the drop cell.
 * Split from TruncatedDropCell to keep file size within limits.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { cn } from '@/lib/utils';
import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { DropEmojiTag } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared/utils/drop-tag-display';

/** Default tag for drops not created in our app (from SMSWiser). */
const DEFAULT_TAG_NAME = 'SMS';

export const DropCellTagChips: FC<{
  staticTag: DropsWithStatsRow['drop']['staticTag'];
  /** Performance tags from drop-performance-tags API */
  performanceTags?: string[];
  /** Source-rule emojis — bare, slightly larger, no chip background. */
  emojiTags?: DropEmojiTag[];
  /** Custom colors for performance tags (rule name -> hex) */
  tagColors?: Record<string, string>;
}> = ({ staticTag, performanceTags, emojiTags, tagColors }) => {
  const tagName = staticTag?.name ?? DEFAULT_TAG_NAME;
  return (
    <>
      {(emojiTags?.length ?? 0) > 0 ? (
        <span className="inline-flex shrink-0 items-center gap-0.5">
          {emojiTags!.map((t) => (
            <span
              key={t.title}
              className="text-sm font-semibold leading-none"
              style={t.color ? { color: t.color } : undefined}
              title={t.title}
            >
              {t.emoji}
            </span>
          ))}
        </span>
      ) : null}
      {(performanceTags?.length ?? 0) > 0 ? (
        <LeadsListTags
          performanceTags={performanceTags ?? []}
          tagColors={tagColors}
          className="inline-flex"
          compact
        />
      ) : null}
      <span
        className={cn(
          'inline-flex items-center justify-center rounded px-1 py-px text-[10px] font-medium leading-none',
          staticTag ? 'text-white' : 'bg-muted text-muted-foreground',
        )}
        style={staticTag ? { backgroundColor: staticTag.color } : undefined}
        title={staticTag ? tagName : `External drop (${tagName})`}
      >
        {tagName}
      </span>
    </>
  );
};
